import {useMemo} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {W3_1Page} from '@/pages/components/audit-program/w-section/constants';
import {W3_1_PAGE} from '@/pages/components/audit-program/w-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface W3_1ComponentsViewProps {
    header: AuditProgramSectionVO;
    page: W3_1Page;
    refCode?: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {...cellBorder, fontWeight: 600, fontSize: '0.7rem', py: 0.6, px: 0.3, lineHeight: 1.25};
const bodySx = {...cellBorder, py: 0.35, px: 0.25, verticalAlign: 'top' as const};
const fieldSx = {minWidth: 0, width: '100%', maxWidth: '100%'};

/** W3.1 — Analysis of components. */
export default function W3_1ComponentsView({
    header,
    page,
    refCode = 'W3.1',
    values,
    onChange,
}: W3_1ComponentsViewProps) {
    const meta = {
        title: page.title || W3_1_PAGE.title,
        note: page.note || W3_1_PAGE.note,
        blankRows: page.blankRows || W3_1_PAGE.blankRows,
        columns: page.columns?.length ? page.columns : W3_1_PAGE.columns,
    };
    const rowIds = useMemo(
        () => Array.from({length: Math.max(1, meta.blankRows)}, (_, i) => `r${i + 1}`),
        [meta.blankRows],
    );

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />
            <Typography variant="subtitle1" fontWeight={700} sx={{textAlign: 'center', mb: 1, mt: 0.5}}>
                {meta.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 1.5}}>
                {meta.note}
            </Typography>
            <TableContainer sx={{overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 1100, tableLayout: 'fixed'}}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} style={{width: 28}}/>
                            {meta.columns.map((c) => (
                                <TableCell key={c.id} sx={headSx}>{c.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rowIds.map((id, idx) => (
                            <TableRow key={id}>
                                <TableCell sx={bodySx}>{idx + 1}</TableCell>
                                {meta.columns.map((c) => (
                                    <TableCell key={c.id} sx={bodySx}>
                                        <InputField
                                            type="text"
                                            fullWidth
                                            compact
                                            sx={fieldSx}
                                            value={values[`${id}.${c.id}`] ?? ''}
                                            onChange={(e) => onChange(`${id}.${c.id}`, e.target.value)}
                                        />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
